import { useEffect, useRef } from 'react';

/**
 * The Cloudflare Turnstile challenge on the public forms (register, login,
 * forgot-password, and the anonymous intake form). Renders nothing — and
 * reports no token — when no site key is configured, so a build without one
 * behaves exactly as before; the API must then run with BOT_CHECK=off
 * (docs/SCHEMA.md § Auth semantics).
 *
 * `siteKey` overrides the build's key. The intake form's descriptor carries
 * the API's own site key when the API has one configured (and null to mean
 * "use the build's"), so the key the widget renders with is always the one
 * the server will verify against. A site key is public by design; the secret
 * never leaves the API.
 *
 * Managed mode: for nearly everyone the widget resolves on its own without a
 * click. The token is single-use — after a submit the caller bumps
 * `resetKey` so the widget issues a fresh one for the next attempt.
 */
const BUILD_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise = null;

function loadTurnstile() {
	if (typeof window === 'undefined') return Promise.resolve(null);
	if (window.turnstile) return Promise.resolve(window.turnstile);
	if (!scriptPromise) {
		scriptPromise = new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.src = SCRIPT_SRC;
			script.async = true;
			script.defer = true;
			script.onload = () => resolve(window.turnstile || null);
			script.onerror = () => {
				scriptPromise = null;
				reject(new Error('Turnstile failed to load'));
			};
			document.head.appendChild(script);
		});
	}
	return scriptPromise;
}

export const turnstileEnabled = () => !!BUILD_SITE_KEY;

/** The key a widget given `siteKey` would render with, or '' for none. */
export const effectiveSiteKey = (siteKey = null) => siteKey || BUILD_SITE_KEY;

export default function TurnstileWidget({ onToken, resetKey = 0, action = 'form', siteKey = null }) {
	const SITE_KEY = effectiveSiteKey(siteKey);
	const containerRef = useRef(null);
	const widgetIdRef = useRef(null);
	const onTokenRef = useRef(onToken);
	onTokenRef.current = onToken;

	useEffect(() => {
		if (!SITE_KEY) return undefined;
		let alive = true;
		loadTurnstile()
			.then(turnstile => {
				if (!alive || !turnstile || !containerRef.current) return;
				widgetIdRef.current = turnstile.render(containerRef.current, {
					sitekey: SITE_KEY,
					action,
					callback: token => onTokenRef.current?.(token || null),
					'expired-callback': () => onTokenRef.current?.(null),
					'error-callback': () => onTokenRef.current?.(null),
				});
			})
			.catch(() => {
				// The API answers 400 challenge_failed without a token; the form
				// shows that message. Nothing to do here.
			});
		return () => {
			alive = false;
			try {
				if (widgetIdRef.current && window.turnstile) window.turnstile.remove(widgetIdRef.current);
			} catch {
				/* the widget may already be gone */
			}
			widgetIdRef.current = null;
		};
	}, [action, SITE_KEY]);

	useEffect(() => {
		if (!resetKey || !widgetIdRef.current || !window.turnstile) return;
		try {
			window.turnstile.reset(widgetIdRef.current);
		} catch {
			/* a reset on a removed widget is harmless */
		}
		onTokenRef.current?.(null);
	}, [resetKey]);

	if (!SITE_KEY) return null;
	return <div ref={containerRef} className='turnstile-widget' style={{ margin: '8px 0' }} data-testid='turnstile' />;
}
